import { Injectable } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import { lastValueFrom } from "rxjs";
import { Organisation } from "../common/organisation";
import { checkOrganisationExport, OrganisationExport } from "../common/organisation-transfer";
import { RepositoryService } from "../linq-utils/repository.service";
import { DownloadService } from "../model-services/download.service";
import { ConfirmDialogComponent, ConfirmDialogModel } from "../ui-components/dialogs/confirm-dialog.component";
import { UserService } from "../user/user.service";

const COMPONENT = "Organisation transfer service"
// an import saves every node and works out all the insights, which can take a while for a large organisation
const IMPORT_TIMEOUT = 5 * 60 * 1000

// For LINQ administrators: an organisation and all its models as one JSON file, and that file imported as a new
// organisation or into an empty one. What the file holds is set out in common/organisation-transfer.ts
@Injectable({
    providedIn: 'root'
})
export class OrganisationTransferService {

    constructor(private repoService:RepositoryService, private userService:UserService, private downloadService:DownloadService,
        private dialog:MatDialog, private snackBar:MatSnackBar) {
    }

    async exportOrganisation(organisation:Organisation) {
        const data:OrganisationExport = await this.repoService.callSingleAPI(this.repoService.getData(`api/admin/organisations/${organisation._key}/export`),
            COMPONENT, "Export organisation", this.userService.getUser(), organisation)
        if (!data) return
        this.downloadService.downloadFile(JSON.stringify(data), `${organisation.name} organisation export`, "json")
        this.snackBar.open(`Exported ${data.organisation.models.length} models and ${data.nodes.length} nodes`, "close", {duration:5000})
    }

    // a new organisation from a file the administrator picks; resolves to the new organisation's key once it's made
    // (working is told when the server is busy with it, for the page to show it)
    async importAsNewOrganisation(working?:(busy:boolean) => void):Promise<string|undefined> {
        const data = await this.pickFile()
        if (!data) return undefined
        const name = `${data.organisation.name} (imported)`
        const confirmed = await this.confirm("Import as a new organisation", [
            ...this.describe(data),
            `It will be a new organisation called "${name}", with you as its admin. You can rename it and invite people afterwards.`,
        ], "Import")
        if (!confirmed) return undefined

        working?.(true)
        const result = await this.repoService.callSingleAPI(this.repoService.create(`api/admin/organisations/import`, {file: data, name}, IMPORT_TIMEOUT),
            COMPONENT, "Import organisation as new", this.userService.getUser()).finally(() => working?.(false))
        if (result?._key) this.snackBar.open(`Imported "${result.name}"`, "close", {duration:5000})
        return result?._key
    }

    // a file the administrator picks, put into an organisation with nothing in it yet; resolves to whether it was
    async importIntoOrganisation(organisation:Organisation, working?:(busy:boolean) => void):Promise<boolean> {
        const data = await this.pickFile()
        if (!data) return false
        const confirmed = await this.confirm(`Import into ${organisation.name}`, [
            ...this.describe(data),
            `Its models, projects, tags, information assets, emission factors and shared filters go into ${organisation.name}, which keeps its own name, people and billing.`,
        ], "Import", "import")
        if (!confirmed) return false

        working?.(true)
        const result = await this.repoService.callSingleAPI(this.repoService.create(`api/admin/organisations/${organisation._key}/import`, {file: data}, IMPORT_TIMEOUT),
            COMPONENT, "Import into organisation", this.userService.getUser(), organisation).finally(() => working?.(false))
        if (result) this.snackBar.open(`Imported into "${organisation.name}"`, "close", {duration:5000})
        return !!result
    }

    private describe(data:OrganisationExport) {
        const exported = data.exportedDate ? new Date(data.exportedDate).toLocaleDateString() : 'an unknown date'
        const projects = data.organisation.projects.length
        return [
            `"${data.organisation.name}", exported on ${exported}: ${data.organisation.models.length} models${projects ? `, ${projects} project${projects === 1 ? '' : 's'}` : ''} and ${data.nodes.length} nodes.`,
            "People, owners, private filters, billing and backups aren't in the file. Insights are worked out again once it's imported.",
        ]
    }

    // filling an existing organisation is asked to be typed out; a new one only clicked
    private async confirm(title:string, messages:string[], button:string, confirmText?:string) {
        const dialogRef = this.dialog.open(ConfirmDialogComponent, {maxWidth: "480px", data: new ConfirmDialogModel(title, messages, confirmText, false, button, "Cancel")})
        const result = await lastValueFrom(dialogRef.afterClosed())
        return result?.button === button
    }

    // the export file the administrator chooses, read and checked; nothing when they cancel or it isn't one
    private pickFile():Promise<OrganisationExport|undefined> {
        return new Promise(resolve => {
            const input = document.createElement('input')
            input.type = 'file'
            input.accept = '.json,application/json'
            input.onchange = async () => {
                const file = input.files?.[0]
                if (!file) return resolve(undefined)
                try {
                    const data = JSON.parse(await file.text())
                    const problem = checkOrganisationExport(data)
                    if (problem) {
                        this.snackBar.open(problem, "close", {duration:8000})
                        return resolve(undefined)
                    }
                    resolve(data as OrganisationExport)
                }catch {
                    this.snackBar.open("The file isn't valid JSON.", "close", {duration:8000})
                    resolve(undefined)
                }
            }
            input.click()
        })
    }
}
